"use client";

import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { Megaphone, X } from "lucide-react";
import type { Locale } from "@/lib/i18n";
import type { PublicSiteNotice } from "@/lib/platform-public-settings";
import {
  isLearnReaderRoute,
  isPlaygroundRoute,
  isQuizPlayerRoute,
} from "@/lib/full-viewport-routes";
import { SITE_NOTICE_DISMISSAL_KEY, persistSiteNoticeDismissed } from "@/lib/site-notice-dismissal";
import { Container } from "@/components/ui/container";

interface SiteNoticeBarProps {
  notice?: PublicSiteNotice;
  locale: Locale;
}

/**
 * The admin-managed site notice bar (`AD-SN01`, `ADMIN_TASKS.md`), rendered
 * above the site header on every page that isn't its own full-viewport shell.
 * Excluded on Playground and the quiz attempt/results player for the same
 * reason `SiteLayout` renders those bare: both are fixed `100dvh` boxes
 * (`PlaygroundLanguageSeoShell.tsx`), so an unaccounted-for bar above them
 * would clip content rather than push it down. Playground gets the same
 * notice through its own bordered slot instead (`PlaygroundSiteNoticeRow`).
 *
 * Server-rendered with its real height from the first paint — no
 * `visible` state deferred to a `useEffect` — so nothing shifts once the
 * client mounts. The one thing that can't be known on the server, a
 * browser's earlier dismissal, is resolved by a small inline script that
 * runs before paint and hides the (already fully laid out) element rather
 * than by starting the bar hidden and revealing it late.
 */
export function SiteNoticeBar({ notice, locale }: SiteNoticeBarProps) {
  const t = useTranslations("common.siteNotice");
  const pathname = usePathname();

  if (!notice) return null;
  // The Learn reader is the third fixed-viewport shell (`LN-RDR01`): its right
  // pane is the viewport minus its own top bar, so a bar above it clips the
  // editor exactly as it would on the Playground.
  if (isPlaygroundRoute(pathname) || isQuizPlayerRoute(pathname) || isLearnReaderRoute(pathname)) {
    return null;
  }

  // `SITE-SPD04`: `getLocalizedField`'s rule, written here: importing it put
  // the whole courses API in the script every page loads.
  const pick = (en: string | undefined, bn: string | undefined) => (locale === "bn" && bn?.trim() ? bn : en);
  const title = pick(notice.title_en, notice.title_bn);
  const body = pick(notice.body_en, notice.body_bn);
  const linkLabel = pick(notice.linkLabel_en, notice.linkLabel_bn) || t("defaultLinkLabel");
  const barId = `site-notice-bar-${notice.id}`;

  function handleDismiss() {
    persistSiteNoticeDismissed(notice!.id);
    const el = document.getElementById(barId);
    if (el) el.style.display = "none";
  }

  return (
    <div
      id={barId}
      className="w-full border-b border-primary/25 bg-primary/10 text-sm text-[rgb(var(--foreground))]"
      role="status"
    >
      <Container className="flex flex-col gap-2 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="flex min-w-0 flex-1 items-start gap-2 sm:items-center">
          <Megaphone className="mt-0.5 h-4 w-4 shrink-0 text-primary sm:mt-0" aria-hidden />
          <p className="min-w-0 leading-snug">
            <span className="font-semibold">{title}</span>
            {body ? <span> {body}</span> : null}
            {notice.linkUrl ? (
              <>
                {" "}
                <a
                  href={notice.linkUrl}
                  className="font-semibold text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                >
                  {linkLabel}
                </a>
              </>
            ) : null}
          </p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t("dismissAriaLabel")}
          className="inline-flex min-h-[44px] shrink-0 items-center justify-center gap-1 rounded-[var(--radius)] px-3 text-sm font-medium text-[rgb(var(--muted-foreground))] transition hover:bg-[rgb(var(--muted))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:min-h-0"
        >
          <X className="h-4 w-4" aria-hidden />
          {t("dismiss")}
        </button>
      </Container>
      <script
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: `(function(){try{if(window.localStorage.getItem(${JSON.stringify(
            SITE_NOTICE_DISMISSAL_KEY,
          )})===${JSON.stringify(notice.id)}){var el=document.getElementById(${JSON.stringify(
            barId,
          )});if(el)el.style.display="none";}}catch(e){}})();`,
        }}
      />
    </div>
  );
}
